import React from 'react';

import Icon from 'components/DesignSystem/Icon';
import { IconType } from 'components/DesignSystem/Icon/icons';

import './index.scss';

export interface SegmentedOption<T extends string | number = string> {
  value: T;
  /** Rótulo de texto. */
  label?: React.ReactNode;
  /** Ícone do DesignSystem (renderizado com `currentColor`, segue o estado ativo). */
  icon?: IconType;
  /** Ícone customizado (SVG inline com `stroke`/`fill` em `currentColor`). */
  iconNode?: React.ReactNode;
  /** Rótulo acessível — obrigatório em opções apenas-ícone. */
  ariaLabel?: string;
  disabled?: boolean;
  /** `id` do botão (ex.: para devolver o foco a ele quando o popover que ele abre fecha). */
  id?: string;
  /**
   * A opção abre um popover em vez de só marcar um valor (ex.: "Intervalo…" do período de
   * Meus Estudos): `aria-haspopup` do botão. Use junto com `ariaExpanded`.
   */
  ariaHasPopup?: 'dialog' | 'menu' | 'listbox';
  /** Popover da opção aberto (`aria-expanded`). Sem popover, deixe `undefined`. */
  ariaExpanded?: boolean;
  /** `id` do popover que a opção controla (`aria-controls`), enquanto ele está na tela. */
  ariaControls?: string;
}

/**
 * `default`: trilho claro com ativo branco (segmentados mobile).
 * `filled`: botões colados com borda e ativo azul cheio com texto branco, 38 px de
 * altura (filtros do desktop, ex.: Flux Personalizado › Meus Estudos).
 */
export type SegmentedVariant = 'default' | 'filled';

export interface SegmentedProps<T extends string | number = string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  /** `true` (padrão): opções ocupam largura igual. `false`: encolhem ao conteúdo. */
  fill?: boolean;
  size?: 'sm' | 'md';
  /** Visual do controle; o padrão `default` mantém o segmentado mobile atual. */
  variant?: SegmentedVariant;
  ariaLabel?: string;
  ariaLabelledBy?: string;
  className?: string;
}

/**
 * Controle segmentado (N1/N2/N3 = navegação; isto NÃO é navegação — troca de *modo* de
 * visualização). Trilho claro + botão ativo branco com texto navy e sombra leve, conforme
 * `NAV_HIERARCHY.md`. É a fonte única de verdade para todos os segmentados mobile.
 * A variante `filled` atende os segmentados de filtro do desktop.
 */
export default function Segmented<T extends string | number = string>({
  options,
  value,
  onChange,
  fill = true,
  size = 'md',
  variant = 'default',
  ariaLabel,
  ariaLabelledBy,
  className = '',
}: SegmentedProps<T>) {
  const rootClass = [
    'ds-segmented',
    `ds-segmented-${size}`,
    fill ? '' : 'ds-segmented-auto',
    variant === 'filled' ? 'ds-segmented-filled' : '',
    className,
  ].filter(Boolean).join(' ');

  return (
    <div
      className={rootClass}
      role="group"
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledBy}
    >
      {options.map((option) => {
        const active = option.value === value;
        return (
          <button
            key={option.value}
            type="button"
            id={option.id}
            className={`ds-segmented-option${active ? ' active' : ''}`}
            aria-pressed={active}
            aria-label={option.ariaLabel}
            aria-haspopup={option.ariaHasPopup}
            aria-expanded={option.ariaExpanded}
            aria-controls={option.ariaControls}
            disabled={option.disabled}
            onClick={() => onChange(option.value)}
          >
            {option.iconNode}
            {option.icon && <Icon iconType={option.icon} color="currentColor" size="small" />}
            {option.label != null && <span className="ds-segmented-label">{option.label}</span>}
          </button>
        );
      })}
    </div>
  );
}
